import React from 'react';
import {
  Button,
  Checkbox,
  Col,
  Collapse,
  Divider,
  Drawer,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';

const ACTIONS = [
  { key: 'read', label: 'READ', aliases: ['read', 'view', 'list'] },
  { key: 'create', label: 'CREATE', aliases: ['create', 'add', 'insert', 'new'] },
  { key: 'edit', label: 'EDIT', aliases: ['edit', 'update', 'write'] },
  { key: 'delete', label: 'DELETE', aliases: ['delete', 'remove', 'unlink', 'drop'] },
];

const HIDDEN_MANDATORY_VIEW_KEYS = new Set(['login', 'signin', 'app', 'app/home']);

const normalizeText = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const normalizeViewKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '');

const isMandatoryHiddenView = (vistaClave) => {
  const normalized = normalizeViewKey(vistaClave);
  return HIDDEN_MANDATORY_VIEW_KEYS.has(normalized);
};

const getVistaResourceCandidates = (vistaClave) => {
  const raw = String(vistaClave || '').split('/').filter(Boolean);
  const leaf = raw.length > 0 ? raw[raw.length - 1] : '';
  const noParams = leaf.split(':')[0];
  const clean = normalizeText(noParams);

  if (!clean) {
    return [];
  }

  const singular = clean.endsWith('s') ? clean.slice(0, -1) : clean;
  return Array.from(new Set([clean, clean.replace(/_/g, ''), singular]));
};

const getFallbackSectionKey = (vistaClave) => {
  const firstPathToken = String(vistaClave || '')
    .split('/')
    .filter(Boolean)
    .map((part) => normalizeText(part.split(':')[0]))[0];

  return firstPathToken || 'otros';
};

const getSectionLabel = (sectionKey) => {
  const normalized = normalizeText(sectionKey) || 'otros';
  if (normalized === 'otros') {
    return 'Otros';
  }
  return normalized
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const buildResourceRows = (permisos) => {
  const byResource = new Map();

  (permisos || []).forEach((permiso) => {
    const recursoKey = normalizeText(permiso?.recurso);
    if (!recursoKey) {
      return;
    }

    if (!byResource.has(recursoKey)) {
      byResource.set(recursoKey, {
        id: recursoKey,
        recurso: permiso?.recurso || recursoKey,
        acciones: {},
      });
    }

    const row = byResource.get(recursoKey);
    const accionNorm = normalizeText(permiso?.accion);
    ACTIONS.forEach((actionDef) => {
      if (!row.acciones[actionDef.key] && actionDef.aliases.includes(accionNorm)) {
        row.acciones[actionDef.key] = permiso;
      }
    });
  });

  return Array.from(byResource.values()).sort((a, b) =>
    String(a?.recurso || '').localeCompare(String(b?.recurso || ''), 'es', { sensitivity: 'base' })
  );
};

const DrawerRole = ({ open, mode, role, catalogo, vistasCatalogo, onClose, onSave, loading }) => {
  const [roleName, setRoleName] = React.useState('');
  const [roleDescription, setRoleDescription] = React.useState('');
  const [nivelAcceso, setNivelAcceso] = React.useState(undefined);
  const [selectedPermissionIds, setSelectedPermissionIds] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState('');

  const isAdminRole = Number(role?.id) === 100;

  const permisoMetaById = React.useMemo(() => {
    const map = new Map();
    (catalogo?.grupos_permisos || []).forEach((grupo) => {
      const grupoKey = normalizeText(grupo?.clave);
      const grupoLabel = grupo?.nombre || getSectionLabel(grupo?.clave);
      const grupoOrder = Number.isFinite(Number(grupo?.orden)) ? Number(grupo?.orden) : 999;

      (grupo?.permisos || []).forEach((permiso) => {
        map.set(permiso.id, {
          ...permiso,
          recursoNorm: normalizeText(permiso?.recurso),
          accionNorm: normalizeText(permiso?.accion),
          grupoKey,
          grupoLabel,
          grupoOrder,
        });
      });
    });
    return map;
  }, [catalogo]);

  const permisosFlat = React.useMemo(
    () =>
      (catalogo?.grupos_permisos || []).flatMap((grupo) =>
        (grupo?.permisos || []).map((permiso) => ({
          ...permiso,
          recursoNorm: normalizeText(permiso?.recurso),
          accionNorm: normalizeText(permiso?.accion),
          grupoKey: normalizeText(grupo?.clave),
          grupoLabel: grupo?.nombre || getSectionLabel(grupo?.clave),
          grupoOrder: Number.isFinite(Number(grupo?.orden)) ? Number(grupo?.orden) : 999,
        }))
      ),
    [catalogo]
  );

  const permisosByVista = React.useMemo(() => {
    return (vistasCatalogo || []).filter((vista) => !isMandatoryHiddenView(vista?.clave)).map((vista) => {
      const indexByAction = {};
      (vista.permisos || []).forEach((rawPermiso) => {
        const permissionFromCatalog = permisoMetaById.get(rawPermiso?.id);
        const permiso = permissionFromCatalog
          ? { ...rawPermiso, ...permissionFromCatalog }
          : {
              ...rawPermiso,
              recursoNorm: normalizeText(rawPermiso?.recurso),
              accionNorm: normalizeText(rawPermiso?.accion),
            };

        const accionNorm = normalizeText(permiso.accion);
        ACTIONS.forEach((actionDef) => {
          if (!indexByAction[actionDef.key] && actionDef.aliases.includes(accionNorm)) {
            indexByAction[actionDef.key] = permiso;
          }
        });
      });

      // Fallback: si no existe mapeo vista_permisos, intenta resolver desde catálogo por recurso+accion.
      const candidates = getVistaResourceCandidates(vista?.clave);
      ACTIONS.forEach((actionDef) => {
        if (!indexByAction[actionDef.key] && candidates.length > 0) {
          const fallback = permisosFlat.find(
            (permiso) =>
              actionDef.aliases.includes(permiso.accionNorm) &&
              candidates.includes(permiso.recursoNorm)
          );
          if (fallback) {
            indexByAction[actionDef.key] = fallback;
          }
        }
      });

      const firstPermissionWithGroup = ACTIONS
        .map((actionDef) => indexByAction[actionDef.key])
        .find((permiso) => permiso?.grupoKey);

      const sectionKey = firstPermissionWithGroup?.grupoKey || getFallbackSectionKey(vista?.clave);
      const sectionLabel = firstPermissionWithGroup?.grupoLabel || getSectionLabel(sectionKey);
      const sectionOrder = Number.isFinite(Number(firstPermissionWithGroup?.grupoOrder))
        ? Number(firstPermissionWithGroup?.grupoOrder)
        : Number.MAX_SAFE_INTEGER;

      return {
        ...vista,
        sectionKey,
        sectionLabel,
        sectionOrder,
        acciones: indexByAction,
      };
    });
  }, [vistasCatalogo, permisosFlat, permisoMetaById]);

  const vistasGroupedBySection = React.useMemo(() => {
    const grouped = new Map();
    const searchNorm = normalizeText(searchTerm);

    permisosByVista
      .filter((vista) => {
        if (!searchNorm) return true;
        const nombreNorm = normalizeText(vista?.nombre);
        const claveNorm = normalizeText(vista?.clave);
        return nombreNorm.includes(searchNorm) || claveNorm.includes(searchNorm);
      })
      .forEach((vista) => {
        const key = normalizeText(vista?.sectionKey) || 'otros';
        if (!grouped.has(key)) {
          grouped.set(key, []);
        }
        grouped.get(key).push(vista);
      });

    return Array.from(grouped.entries())
      .map(([key, vistas]) => {
        const sortedVistas = [...vistas].sort((a, b) =>
          String(a?.nombre || '').localeCompare(String(b?.nombre || ''), 'es', { sensitivity: 'base' })
        );

        const actionPermissionIds = ACTIONS.reduce((acc, actionDef) => {
          const ids = sortedVistas
            .map((vista) => vista.acciones?.[actionDef.key]?.id)
            .filter(Boolean);
          acc[actionDef.key] = Array.from(new Set(ids));
          return acc;
        }, {});

        const allPermissionIds = Array.from(
          new Set(ACTIONS.flatMap((actionDef) => actionPermissionIds[actionDef.key] || []))
        );

        return {
          sectionKey: key,
          sectionLabel: sortedVistas[0]?.sectionLabel || getSectionLabel(key),
          sectionOrder: Number.isFinite(Number(sortedVistas[0]?.sectionOrder))
            ? Number(sortedVistas[0]?.sectionOrder)
            : Number.MAX_SAFE_INTEGER,
          vistas: sortedVistas,
          actionPermissionIds,
          allPermissionIds,
        };
      })
      .filter((group) => group.vistas.length > 0)
      .sort((a, b) => {
        if (a.sectionOrder !== b.sectionOrder) {
          return a.sectionOrder - b.sectionOrder;
        }
        return a.sectionLabel.localeCompare(b.sectionLabel, 'es', { sensitivity: 'base' });
      });
  }, [permisosByVista, searchTerm]);

  const allVistasPermisoIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          permisosByVista.flatMap((vista) =>
            ACTIONS.map((actionDef) => vista.acciones?.[actionDef.key]?.id).filter(Boolean)
          )
        )
      ),
    [permisosByVista]
  );

  const recursosSinVistaGroupedBySection = React.useMemo(() => {
    const vistaPermissionIds = new Set(allVistasPermisoIds);

    return (catalogo?.grupos_permisos || [])
      .map((grupo) => {
        const permisosSinVista = (grupo?.permisos || []).filter(
          (permiso) => !vistaPermissionIds.has(permiso.id)
        );

        const rows = buildResourceRows(permisosSinVista);
        if (!rows.length) {
          return null;
        }

        const actionPermissionIds = ACTIONS.reduce((acc, actionDef) => {
          const ids = rows.map((row) => row.acciones?.[actionDef.key]?.id).filter(Boolean);
          acc[actionDef.key] = Array.from(new Set(ids));
          return acc;
        }, {});

        const allPermissionIds = Array.from(
          new Set(ACTIONS.flatMap((actionDef) => actionPermissionIds[actionDef.key] || []))
        );

        const groupKey = normalizeText(grupo?.clave) || 'otros';
        const groupOrder = Number.isFinite(Number(grupo?.orden)) ? Number(grupo?.orden) : 999;

        return {
          sectionKey: `resource_${groupKey}`,
          sectionLabel: `${grupo?.nombre || getSectionLabel(groupKey)} (Recursos API)`,
          sectionOrder: groupOrder,
          rows,
          actionPermissionIds,
          allPermissionIds,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.sectionOrder !== b.sectionOrder) {
          return a.sectionOrder - b.sectionOrder;
        }
        return a.sectionLabel.localeCompare(b.sectionLabel, 'es', { sensitivity: 'base' });
      });
  }, [catalogo, allVistasPermisoIds]);

  const selectableSectionsByKey = React.useMemo(
    () =>
      new Map(
        [...vistasGroupedBySection, ...recursosSinVistaGroupedBySection].map((section) => [
          section.sectionKey,
          section,
        ])
      ),
    [vistasGroupedBySection, recursosSinVistaGroupedBySection]
  );

  React.useEffect(() => {
    if (!open) {
      setSearchTerm('');
      return;
    }

    setRoleName(role?.name || '');
    setRoleDescription(role?.descripcion || '');
    setNivelAcceso(role?.nivel_acceso_maximo_id || undefined);
    const rolePermisos = (role?.permisos || []).map((item) => item.id);
    if (isAdminRole) {
      const allPermisos = Array.from(
        new Set([
          ...rolePermisos,
          ...allVistasPermisoIds,
          ...((catalogo?.grupos_permisos || []).flatMap((grupo) =>
            (grupo?.permisos || []).map((permiso) => permiso.id)
          ) || []),
        ])
      );
      setSelectedPermissionIds(allPermisos);
    } else {
      setSelectedPermissionIds(rolePermisos);
    }
  }, [open, role, catalogo, isAdminRole, allVistasPermisoIds]);

  const onTogglePermiso = React.useCallback((permisoId, checked) => {
    if (isAdminRole) {
      return;
    }
    setSelectedPermissionIds((prevState) => {
      if (checked) {
        if (prevState.includes(permisoId)) {
          return prevState;
        }
        return [...prevState, permisoId];
      }
      return prevState.filter((id) => id !== permisoId);
    });
  }, [isAdminRole]);

  const onToggleSectionAction = React.useCallback((sectionKey, actionKey) => {
    if (isAdminRole) {
      return;
    }

    const section = selectableSectionsByKey.get(sectionKey);
    if (!section) {
      return;
    }

    const targetIds = actionKey === 'all'
      ? section.allPermissionIds
      : (section.actionPermissionIds?.[actionKey] || []);

    if (!targetIds.length) {
      return;
    }

    setSelectedPermissionIds((prevState) => {
      const hasAll = targetIds.every((id) => prevState.includes(id));
      if (hasAll) {
        return prevState.filter((id) => !targetIds.includes(id));
      }

      const merged = new Set(prevState);
      targetIds.forEach((id) => merged.add(id));
      return Array.from(merged);
    });
  }, [isAdminRole, selectableSectionsByKey]);

  const handleSave = () => {
    onSave({
      id: role?.id,
      name: roleName.trim(),
      descripcion: roleDescription.trim(),
      nivel_acceso_maximo_id: nivelAcceso,
      permiso_ids: isAdminRole
        ? Array.from(
            new Set([
              ...selectedPermissionIds,
              ...allVistasPermisoIds,
              ...((catalogo?.grupos_permisos || []).flatMap((grupo) =>
                (grupo?.permisos || []).map((permiso) => permiso.id)
              ) || []),
            ])
          )
        : selectedPermissionIds,
    });
  };

  const niveles = catalogo?.niveles_acceso || [];

  return (
    <Drawer
      title={mode === 'edit' ? `Editar Rol: ${role?.name || ''}` : 'Agregar Rol'}
      open={open}
      width={980}
      destroyOnClose
      onClose={onClose}
      extra={
        <Space>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="primary" onClick={handleSave} loading={loading}>
            Guardar Cambios
          </Button>
        </Space>
      }
    >
      <Row gutter={12}>
        <Col span={10}>
          <label>Nombre del Rol</label>
          <Input
            placeholder="Ej. Coordinador de Nomina"
            value={roleName}
            onChange={(event) => setRoleName(event.target.value)}
            style={{ marginTop: 6 }}
          />
        </Col>
        <Col span={10}>
          <label>Descripcion</label>
          <Input
            placeholder="Alcance del rol"
            value={roleDescription}
            onChange={(event) => setRoleDescription(event.target.value)}
            style={{ marginTop: 6 }}
          />
        </Col>
        <Col span={4}>
          <label>Nivel Maximo</label>
          <Select
            style={{ width: '100%', marginTop: 6 }}
            value={nivelAcceso}
            onChange={setNivelAcceso}
            placeholder="Nivel"
            options={niveles.map((nivel) => ({
              value: nivel.id,
              label: `${nivel.nombre} (${nivel.valor})`,
            }))}
          />
        </Col>
      </Row>

      <Divider />

      {isAdminRole ? (
        <Tag color="gold">Administrador: acceso total habilitado</Tag>
      ) : null}

      <Divider style={{ marginTop: 12 }} />

      <Input
        placeholder="Buscar vista por nombre o clave..."
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        style={{ marginBottom: 12 }}
      />

      {(vistasCatalogo || []).length === 0 ? (
        <Empty description="No hay vistas configuradas" />
      ) : (
        <Collapse
          defaultActiveKey={vistasGroupedBySection.map((group) => group.sectionKey)}
          items={vistasGroupedBySection.map((group) => {
            const allInSectionChecked =
              group.allPermissionIds.length > 0 &&
              group.allPermissionIds.every((id) => selectedPermissionIds.includes(id));

            return {
              key: group.sectionKey,
              label: `${group.sectionLabel} (${group.vistas.length})`,
              extra: (
                <Space onClick={(event) => event.stopPropagation()}>
                  <Button
                    size="small"
                    type={allInSectionChecked ? 'primary' : 'default'}
                    disabled={isAdminRole || group.allPermissionIds.length === 0}
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleSectionAction(group.sectionKey, 'all');
                    }}
                  >
                    TODOS
                  </Button>

                  {ACTIONS.map((actionDef) => {
                    const ids = group.actionPermissionIds?.[actionDef.key] || [];
                    const allActionChecked =
                      ids.length > 0 && ids.every((id) => selectedPermissionIds.includes(id));

                    return (
                      <Button
                        key={`${group.sectionKey}_${actionDef.key}`}
                        size="small"
                        type={allActionChecked ? 'primary' : 'default'}
                        disabled={isAdminRole || ids.length === 0}
                        onClick={(event) => {
                          event.stopPropagation();
                          onToggleSectionAction(group.sectionKey, actionDef.key);
                        }}
                      >
                        {actionDef.label}
                      </Button>
                    );
                  })}
                </Space>
              ),
              children: (
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={group.vistas}
                  columns={[
                    {
                      title: 'Vista',
                      key: 'vista',
                      render: (_, item) => (
                        <Space direction="vertical" size={0}>
                          <Typography.Text strong>{item.nombre}</Typography.Text>
                          <Typography.Text type="secondary">{item.clave}</Typography.Text>
                        </Space>
                      ),
                    },
                    ...ACTIONS.map((actionDef) => ({
                      title: actionDef.label,
                      key: actionDef.key,
                      align: 'center',
                      width: 100,
                      render: (_, item) => {
                        const permiso = item.acciones?.[actionDef.key];
                        if (!permiso) {
                          return <Tag>-</Tag>;
                        }
                        const checked = selectedPermissionIds.includes(permiso.id) || isAdminRole;
                        return (
                          <Checkbox
                            checked={checked}
                            disabled={isAdminRole}
                            onChange={(event) => onTogglePermiso(permiso.id, event.target.checked)}
                          />
                        );
                      },
                    })),
                  ]}
                />
              ),
            };
          })}
        />
      )}

      {recursosSinVistaGroupedBySection.length > 0 ? (
        <>
          <Divider style={{ marginTop: 20 }} />
          <Typography.Title level={5} style={{ marginBottom: 10 }}>
            Permisos Generales de Recursos
          </Typography.Title>
          <Collapse
            defaultActiveKey={recursosSinVistaGroupedBySection.map((group) => group.sectionKey)}
            items={recursosSinVistaGroupedBySection.map((group) => {
              const allInSectionChecked =
                group.allPermissionIds.length > 0 &&
                group.allPermissionIds.every((id) => selectedPermissionIds.includes(id));

              return {
                key: group.sectionKey,
                label: `${group.sectionLabel} (${group.rows.length})`,
                extra: (
                  <Space onClick={(event) => event.stopPropagation()}>
                    <Button
                      size="small"
                      type={allInSectionChecked ? 'primary' : 'default'}
                      disabled={isAdminRole || group.allPermissionIds.length === 0}
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggleSectionAction(group.sectionKey, 'all');
                      }}
                    >
                      TODOS
                    </Button>

                    {ACTIONS.map((actionDef) => {
                      const ids = group.actionPermissionIds?.[actionDef.key] || [];
                      const allActionChecked =
                        ids.length > 0 && ids.every((id) => selectedPermissionIds.includes(id));

                      return (
                        <Button
                          key={`${group.sectionKey}_${actionDef.key}`}
                          size="small"
                          type={allActionChecked ? 'primary' : 'default'}
                          disabled={isAdminRole || ids.length === 0}
                          onClick={(event) => {
                            event.stopPropagation();
                            onToggleSectionAction(group.sectionKey, actionDef.key);
                          }}
                        >
                          {actionDef.label}
                        </Button>
                      );
                    })}
                  </Space>
                ),
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={group.rows}
                    columns={[
                      {
                        title: 'Recurso',
                        key: 'recurso',
                        render: (_, item) => <Typography.Text strong>{item.recurso}</Typography.Text>,
                      },
                      ...ACTIONS.map((actionDef) => ({
                        title: actionDef.label,
                        key: actionDef.key,
                        align: 'center',
                        width: 100,
                        render: (_, item) => {
                          const permiso = item.acciones?.[actionDef.key];
                          if (!permiso) {
                            return <Tag>-</Tag>;
                          }
                          const checked = selectedPermissionIds.includes(permiso.id) || isAdminRole;
                          return (
                            <Checkbox
                              checked={checked}
                              disabled={isAdminRole}
                              onChange={(event) => onTogglePermiso(permiso.id, event.target.checked)}
                            />
                          );
                        },
                      })),
                    ]}
                  />
                ),
              };
            })}
          />
        </>
      ) : null}
    </Drawer>
  );
};

export default DrawerRole;
